import type { ReactNode } from "react";
import type { GridOverride } from "../../components/dataGrid/gridOverrides.tsx";
import type { GenericPrice } from "../../api/price.ts";
import {
  PRICE_COLUMN_TO_TYPE, PRICE_COLUMNS, calcTaxPairs,
  type PendingEdits, type PendingRow, type PriceScope, type PriceTaxPair,
} from "./advancedPriceShared.ts";
import {
  fmtPrice, describeWeight, makeGenericPrice, EPS,
  WEIGHT_KIOSK_PRODUCT, WEIGHT_LOCATION_PRODUCT,
} from "../../util/pricingCascade.ts";
import { effectiveRate, type PriceTaxMode } from "../../util/priceTaxDerive.ts";
import { CalcPriceCell } from "./advancedPriceGridOverride.tsx";
import styles from "./advancedPriceGridPage.module.scss";

export type PriceTabScope =
  | { kind: "kiosk"; kioskId: number }
  | { kind: "location"; locationId: number };

/** Override column name in the grid = "Override<type>" (matches the TVF). */
const overrideCol = (pt: number) => `Override${pt}`;

/** Read a numeric cell by (case-insensitive) column name; "" / NULL → null. */
function num(rowData: unknown[], colIndex: Record<string, number>, name: string): number | null {
  for (const k of Object.keys(colIndex)) {
    if (k.toLowerCase() === name.toLowerCase()) {
      const v = rowData[colIndex[k]!];
      if (v == null || String(v).trim() === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

/** Read a string cell by (case-insensitive) column name; "" / NULL → "". */
function str(rowData: unknown[], colIndex: Record<string, number>, name: string): string {
  for (const k of Object.keys(colIndex)) {
    if (k.toLowerCase() === name.toLowerCase()) {
      const v = rowData[colIndex[k]!];
      return v == null ? "" : String(v).trim();
    }
  }
  return "";
}

/** Full PriceScope for a product at the tab's fixed kiosk/location scope.
 *  PendingRow.scope is a full PriceScope (all 9 numeric dims); fill the rest
 *  with 0 — makeGenericPrice coerces 0 → null (unset) on save. */
function fullScopeFor(scope: PriceTabScope, productId: number): PriceScope {
  return {
    Product: productId, Type: 0, Category: 0,
    Kiosk: scope.kind === "kiosk" ? scope.kioskId : 0,
    Location: scope.kind === "location" ? scope.locationId : 0,
    Chain: 0, District: 0, State: 0, OperatorID: 0,
  };
}

export function buildPriceTabGridOverride(
  scope: PriceTabScope,
  pendingRef: { current: PendingEdits },
  onEdited: () => void,
  productMap: Map<number, string>,
  onDiffClick?: (productId: number) => void,
  priceMode: PriceTaxMode = "direct",
  priceRate = "",
  hideLateTaxRate = false,
): GridOverride {
  const priceCols = new Set(PRICE_COLUMNS.map((c) => overrideCol(PRICE_COLUMN_TO_TYPE[c]!).toLowerCase()));
  const calc = priceMode !== "direct";
  // Price PriceType → its pair. Drops the Reserve-Late pair when the Late Taxrate is
  // hidden, so PT7 stays a direct column instead of exposing the hidden rate in calc mode.
  const pairs = calcTaxPairs(hideLateTaxRate);
  const calcPairByPricePT = new Map<number, PriceTaxPair>(pairs.map((p) => [p.priceType, p]));
  // Original input value (+rate) per product/pair, captured once from the server
  // data so a revert-to-original clears the stage across cell remounts.
  const baseline = new Map<string, { input: number; rate: number | null }>();

  const keyFor = (productId: number) =>
    scope.kind === "kiosk" ? `K${scope.kioskId}|P${productId}` : `L${scope.locationId}|P${productId}`;

  const cellDecorators: NonNullable<GridOverride["cellDecorators"]> = {};

  // Product cell: "#id name", with a hover tooltip carrying id + name + ext item.
  cellDecorators["Product"] = (_node: ReactNode, ctx) => {
    const id = num(ctx.rowData, ctx.colIndex, "Product") ?? 0;
    const name = productMap.get(id) ?? "";
    const ext = str(ctx.rowData, ctx.colIndex, "ProductRef");
    const tip = [`#${id}`, name, ext ? `Ext item: ${ext}` : ""].filter(Boolean).join(" · ");
    return (
      <span title={tip}>
        <span className={styles.productId}>#{id}</span>
        {name && <span className={styles.productName}>{name}</span>}
      </span>
    );
  };

  // Source cell: decode the winning Price row's specificity weight into the scope
  // dimensions it's built from (e.g. "Product + Location"), with a plain-language
  // tooltip explaining where the current price resolves from.
  cellDecorators["SourceWeight"] = (_node: ReactNode, ctx) => {
    const w = num(ctx.rowData, ctx.colIndex, "SourceWeight") ?? 0;
    const label = w ? describeWeight(w) : "—";
    const tip = w
      ? `Current price comes from a ${describeWeight(w)}-level override — the most specific matching override wins.`
      : "No matching override — using the product default.";
    return <span className={styles.scopeKind} title={tip}>{label}</span>;
  };

  // DiffCount cell (location scope): a button that drills into the divergent
  // kiosks for this product. Rendered only when a callback + a positive count.
  cellDecorators["DiffCount"] = (node: ReactNode, ctx) => {
    if (!onDiffClick) return node;
    const productId = num(ctx.rowData, ctx.colIndex, "Product") ?? 0;
    const count = num(ctx.rowData, ctx.colIndex, "DiffCount") ?? 0;
    if (count <= 0) return <span className={styles.scopeDefault}>—</span>;
    return (
      <button
        type="button"
        className={styles.diffCountBtn}
        title="Kiosks whose actual price differs from the location price"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDiffClick(productId); }}
      >
        {count} kiosk{count === 1 ? "" : "s"} differ
      </button>
    );
  };

  // One decorator per override column. In a calc mode the price columns become
  // net/gross editors (CalcPriceCell) and the paired tax columns are hidden by the
  // page; otherwise paint the pending value, else the default node.
  for (const col of PRICE_COLUMNS) {
    const pt = PRICE_COLUMN_TO_TYPE[col]!;
    cellDecorators[overrideCol(pt)] = (node: ReactNode, ctx) => {
      const productId = num(ctx.rowData, ctx.colIndex, "Product") ?? 0;
      const key = keyFor(productId);
      const pending = pendingRef.current.get(key);
      const pair = calcPairByPricePT.get(pt);

      if (calc && pair) {
        const taxPT = pair.taxType;
        const stagedPrice = pending && pt in pending.prices ? pending.prices[pt] : undefined;
        const stagedTax = pending && taxPT in pending.prices ? pending.prices[taxPT] : undefined;
        const origPrice = num(ctx.rowData, ctx.colIndex, overrideCol(pt));
        const origTax = num(ctx.rowData, ctx.colIndex, overrideCol(taxPT));
        const curPrice = stagedPrice !== undefined ? stagedPrice : origPrice;
        const curTax = stagedTax !== undefined ? stagedTax : origTax;
        const staged = stagedPrice !== undefined || stagedTax !== undefined;
        const taxIsRate = !!pair.taxIsRate;

        const bkey = `${key}:${pt}`;
        const origInput = priceMode === "gross"
          ? (taxIsRate
              ? (origPrice != null ? origPrice * (1 + (origTax ?? 0) / 100) : null)
              : (origPrice != null && origTax != null ? origPrice + origTax : origPrice))
          : origPrice;
        const origRate = taxIsRate
          ? origTax
          : (origPrice != null && origTax != null && origPrice !== 0 ? effectiveRate(origPrice, origTax) : null);
        if (origInput != null && !baseline.has(bkey)) baseline.set(bkey, { input: origInput, rate: origRate });
        const baseRec = baseline.get(bkey);

        return (
          <CalcPriceCell
            key={`calc-${pt}-${priceMode}-${priceRate}-${staged ? "s" : "o"}-${curPrice ?? ""}-${curTax ?? ""}`}
            mode={priceMode}
            rate={priceRate}
            pricePT={pt}
            taxPT={taxPT}
            taxIsRate={taxIsRate}
            scope={fullScopeFor(scope, productId)}
            keyOverride={key}
            curPrice={curPrice}
            curTax={curTax}
            origPrice={origPrice}
            origTax={origTax}
            baselineInput={baseRec?.input ?? null}
            baselineRate={baseRec?.rate ?? null}
            staged={staged}
            pendingRef={pendingRef}
            onEdited={onEdited}
          />
        );
      }

      // Reserve Period (PT9) is taxed at the Rental Tax Rate (PT4, the Rental column).
      // In calc mode fold it into a net/gross editor that READS that rate (read-only —
      // edit it in the Rental column) and stores ONLY PT9, so it respects Net/Gross.
      if (pt === 9) {
        const title = "Net period price — taxed at the Rental Tax Rate (the Rental column), like the editor's Reserve group. If left blank, the kiosk falls back to the Rental Price.";
        if (calc) {
          const stagedPrice = pending && 9 in pending.prices ? pending.prices[9] : undefined;
          const stagedRate = pending && 4 in pending.prices ? pending.prices[4] : undefined;
          const origPeriod = num(ctx.rowData, ctx.colIndex, overrideCol(9));
          const ratev = stagedRate !== undefined ? stagedRate : num(ctx.rowData, ctx.colIndex, overrideCol(4));
          const curPeriod = stagedPrice !== undefined ? stagedPrice : origPeriod;
          const stagedP = stagedPrice !== undefined;
          const bkey = `${key}:9`;
          const origInput = priceMode === "gross"
            ? (origPeriod != null ? origPeriod * (1 + (ratev ?? 0) / 100) : null)
            : origPeriod;
          if (origInput != null && !baseline.has(bkey)) baseline.set(bkey, { input: origInput, rate: ratev });
          const baseRec = baseline.get(bkey);
          return (
            <CalcPriceCell
              key={`calc-9-${priceMode}-${priceRate}-${stagedP ? "s" : "o"}-${curPeriod ?? ""}-${ratev ?? ""}`}
              mode={priceMode}
              rate={priceRate}
              pricePT={9}
              taxPT={4}
              taxIsRate
              rateReadOnly
              scope={fullScopeFor(scope, productId)}
              keyOverride={key}
              curPrice={curPeriod}
              curTax={ratev}
              origPrice={origPeriod}
              origTax={ratev}
              baselineInput={baseRec?.input ?? null}
              baselineRate={baseRec?.rate ?? null}
              staged={stagedP}
              pendingRef={pendingRef}
              onEdited={onEdited}
            />
          );
        }
        if (pending && 9 in pending.prices) {
          const v = pending.prices[9];
          return <span title={title} className={v === null ? styles.cellDeleted : styles.cellChanged}>{v === null ? "—" : fmtPrice(v as number)}</span>;
        }
        return <span title={title}>{node}</span>;
      }

      if (pending && pt in pending.prices) {
        const v = pending.prices[pt];
        return (
          <span className={v === null ? styles.cellDeleted : styles.cellChanged}>
            {v === null ? "—" : fmtPrice(v as number)}
          </span>
        );
      }
      return node;
    };
  }

  // In calc mode our CalcPriceCell drives the paired price columns — lock the grid's
  // native inline editor on them (the tax columns are hidden by the page).
  const cellLocked: NonNullable<GridOverride["cellLocked"]> = {};
  if (calc) {
    for (const p of pairs) cellLocked[overrideCol(p.priceType)] = () => true;
    cellLocked[overrideCol(9)] = () => true; // Reserve Period: driven by its read-only-rate calc cell
  }

  return {
    cellDecorators,
    cellLocked,
    onCellEditCommit: (ctx) => {
      if (!priceCols.has(ctx.colName.toLowerCase())) return { kind: "default" };
      const ptStr = ctx.colName.replace(/[^0-9]/g, "");
      const pt = Number(ptStr);
      if (!Number.isFinite(pt) || pt === 0) return { kind: "skip" };
      const productId = num(ctx.rowData, ctx.colIndex, "Product") ?? 0;
      const key = keyFor(productId);
      const raw = String(ctx.newValue ?? "").trim();
      const value: number | null = raw === "" ? null : Number(raw);
      if (value !== null && (!Number.isFinite(value) || value < 0)) return { kind: "skip" };

      // Compare to the ORIGINAL server value so a no-op edit (re-typing the
      // existing override, or reverting a pending change) never leaves a
      // pending entry behind.
      const origIdx = ctx.colIndex[ctx.colName];
      const origRaw = origIdx != null ? ctx.rowData[origIdx] : undefined;
      const origStr = origRaw == null ? "" : String(origRaw).trim();
      const orig: number | null = origStr === "" ? null : Number(origStr);
      const unchanged =
        (value === null && orig === null) ||
        (value !== null && orig !== null && Math.abs(value - orig) < EPS);

      const prev = pendingRef.current.get(key);
      if (unchanged) {
        if (prev && pt in prev.prices) {
          delete prev.prices[pt];
          if (Object.keys(prev.prices).length === 0) pendingRef.current.delete(key);
        }
      } else {
        const row: PendingRow = prev ?? { scope: fullScopeFor(scope, productId), prices: {} };
        row.prices[pt] = value;
        pendingRef.current.set(key, row);
      }
      onEdited();
      return { kind: "local", value: value === null ? "" : value };
    },
  };
}

/** Build GenericPrice[] for saveAdvancedPriceData from a price-tab pendingRef. */
export function buildPriceTabSaveItems(scope: PriceTabScope, pending: PendingEdits): GenericPrice[] {
  const weight = scope.kind === "kiosk" ? WEIGHT_KIOSK_PRODUCT : WEIGHT_LOCATION_PRODUCT;
  const items: GenericPrice[] = [];
  for (const { scope: s, prices } of pending.values()) {
    for (const [ptStr, value] of Object.entries(prices)) {
      items.push(makeGenericPrice(s, Number(ptStr), value, weight));
    }
  }
  return items;
}
